import { createI18n } from 'vue-i18n';
import { LOCALES, type Locale } from 'shared';
import en from './locales/en.json';
import es from './locales/es.json';

const STORAGE_KEY = '3ojo.locale';

/**
 * Interface language. Note this is separate from search: the card filter
 * matches English and Spanish terms whichever language the UI is in, because
 * the SPEC asks for the filter to be bilingual regardless of the i18n choice.
 */
export function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LOCALES.includes(stored as Locale)) return stored as Locale;
  } catch {
    // Storage refused; fall through to the browser's preference.
  }

  const preferred = navigator.languages ?? [navigator.language];
  for (const tag of preferred) {
    const base = tag.slice(0, 2).toLowerCase();
    if (LOCALES.includes(base as Locale)) return base as Locale;
  }

  return 'en';
}

export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: 'en',
  messages: { en, es },
  // The interpolation style used throughout the message files is {name}.
  missingWarn: import.meta.env.DEV,
  fallbackWarn: false,
});

export function setLocale(locale: Locale): void {
  i18n.global.locale.value = locale;
  document.documentElement.lang = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Non-fatal: the choice just will not survive a reload.
  }
}

export const currentLocale = (): Locale => i18n.global.locale.value as Locale;
